/**
 * Connection state for the offline / "Reconnecting…" banner:
 * - "offline": the browser says there's no network;
 * - "reconnecting": requests are failing at the network level or Carno is
 *   briefly unavailable (502/503/504, e.g. during a restart);
 * - "online": all good.
 * `useOnline()` is false in both bad states (network actions get disabled).
 */
import { useSyncExternalStore } from "react";

export type ConnectionState = "online" | "offline" | "reconnecting";

type Listener = () => void;

const listeners = new Set<Listener>();
let browserOnline = typeof navigator === "undefined" ? true : navigator.onLine;
let failing = false;

function emit(): void {
  for (const l of listeners) l();
}

if (typeof window !== "undefined") {
  window.addEventListener("online", () => {
    browserOnline = true;
    emit();
  });
  window.addEventListener("offline", () => {
    browserOnline = false;
    emit();
  });
}

/** A request failed at the network level or Carno was briefly unavailable. */
export function reportNetworkFailure(): void {
  if (!failing) {
    failing = true;
    emit();
  }
}

export function reportNetworkSuccess(): void {
  if (failing) {
    failing = false;
    emit();
  }
}

export function connectionState(): ConnectionState {
  if (!browserOnline) return "offline";
  if (failing) return "reconnecting";
  return "online";
}

export function isOnline(): boolean {
  return connectionState() === "online";
}

export function subscribeConnection(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useConnectionState(): ConnectionState {
  return useSyncExternalStore(subscribeConnection, connectionState, () => "online" as ConnectionState);
}

export function useOnline(): boolean {
  return useConnectionState() === "online";
}
